import api, { Base, Tags } from 'ev-api/api';

import {
  CdxModuleInstance,
  CdxModuleInstancesListResponse,
  CdxProxyEnvelope,
  CreateModuleInstanceParams,
  DeleteModuleInstanceParams,
  GetModuleInstanceParams,
  ListModuleInstancesParams,
  ToggleModuleInstanceParams,
  UpdateModuleInstanceParams,
} from './module-types';

const cdxModuleInstancesApi = api.injectEndpoints({
  endpoints: builder => ({
    listModuleInstances: builder.query<
      CdxModuleInstance[],
      ListModuleInstancesParams
    >({
      query: ({ handle, customerId }) => ({
        url: `${Base.V4}/cdx_proxy/config/modules/${handle}/instances`,
        params: {
          'filter[customer_owner_type]': 'evisit_customer',
          'filter[customer_owner_identifier]': customerId,
          page_size: 2000,
        },
      }),
      transformResponse: (response: CdxModuleInstancesListResponse) =>
        response.data.module_instances,
      providesTags: [Tags.CdxModuleInstances],
    }),

    getModuleInstance: builder.query<
      CdxModuleInstance,
      GetModuleInstanceParams
    >({
      query: ({ handle, instanceId }) => ({
        url: `${Base.V4}/cdx_proxy/config/modules/${handle}/instances/${instanceId}`,
      }),
      transformResponse: (response: CdxProxyEnvelope<CdxModuleInstance>) =>
        response.data,
      providesTags: [Tags.CdxModuleInstances],
    }),

    createModuleInstance: builder.mutation<
      CdxModuleInstance,
      CreateModuleInstanceParams
    >({
      query: ({ handle, name, customerOwner, instanceOwner, params }) => ({
        url: `${Base.V4}/cdx_proxy/config/modules/${handle}/instances`,
        method: 'POST',
        body: {
          name,
          customer_owner: customerOwner,
          ...(instanceOwner ? { instance_owner: instanceOwner } : {}),
          params,
        },
      }),
      transformResponse: (response: CdxProxyEnvelope<CdxModuleInstance>) =>
        response.data,
      invalidatesTags: [Tags.CdxModuleInstances],
    }),

    updateModuleInstance: builder.mutation<
      CdxModuleInstance,
      UpdateModuleInstanceParams
    >({
      query: ({ handle, instanceId, name, params }) => ({
        url: `${Base.V4}/cdx_proxy/config/modules/${handle}/instances/${instanceId}`,
        method: 'PATCH',
        body: {
          ...(name !== undefined ? { name } : {}),
          params,
        },
      }),
      transformResponse: (response: CdxProxyEnvelope<CdxModuleInstance>) =>
        response.data,
      invalidatesTags: [Tags.CdxModuleInstances],
    }),

    deleteModuleInstance: builder.mutation<void, DeleteModuleInstanceParams>({
      query: ({ handle, instanceId }) => ({
        url: `${Base.V4}/cdx_proxy/config/modules/${handle}/instances/${instanceId}`,
        method: 'DELETE',
      }),
      invalidatesTags: [Tags.CdxModuleInstances],
    }),

    enableModuleInstance: builder.mutation<void, ToggleModuleInstanceParams>({
      query: ({ handle, instanceId }) => ({
        url: `${Base.V4}/cdx_proxy/config/modules/${handle}/instances/${instanceId}/enable`,
        method: 'PATCH',
      }),
      invalidatesTags: [Tags.CdxModuleInstances],
    }),

    disableModuleInstance: builder.mutation<void, ToggleModuleInstanceParams>({
      query: ({ handle, instanceId }) => ({
        url: `${Base.V4}/cdx_proxy/config/modules/${handle}/instances/${instanceId}/disable`,
        method: 'PATCH',
      }),
      invalidatesTags: [Tags.CdxModuleInstances],
    }),
  }),
});

export const {
  useListModuleInstancesQuery,
  useGetModuleInstanceQuery,
  useCreateModuleInstanceMutation,
  useUpdateModuleInstanceMutation,
  useDeleteModuleInstanceMutation,
  useEnableModuleInstanceMutation,
  useDisableModuleInstanceMutation,
} = cdxModuleInstancesApi;
